export interface SuperAdminBulkLookupOption {
  id: string | number;
  name: string;
}

export interface SuperAdminBulkHealthColorOption {
  id: string;
  name: string;
  label?: string;
  colorCode?: string;
}

export interface SuperAdminBulkIngredientDraft {
  name: string;
  quantity: number;
  approximateGrams: number;
  unitLabel?: string;
}

export interface SuperAdminBulkRecipeIngredient {
  ingredientId: number;
  /** Preferred IngredientRefs.Id for RecipeIngredients FK (when known). */
  ingredientRefId?: number | null;
  ingredientName: string;
  servingUnitMasterId: number;
  unitLabel: string;
  approximateValue: number;
  quantity: number;
  totalGrams: number;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  /** From matched master ingredient — used to auto-fill recipe Health Color / Dietary Preference. */
  isVeg?: boolean | null;
  mealPreference?: string;
  healthColorCodeId?: number | null;
  healthColorCodeName?: string;
  colorCode?: number | null;
}

export interface SuperAdminBulkRecipeUom {
  uomId: number;
  categoryId: number;
  categoryName: string;
  subcategoryId: number;
  subcategoryName: string;
  ml: number;
  gram: number;
  valueGram: number;
  isPreferred: boolean;
  name?: string;
  abbreviation?: string;
}

export interface SuperAdminBulkGeneratedFssaiTag {
  fssaiNutrientTagMasterId: number;
  fssaiTagName?: string;
  operator?: string;
  minValue?: number;
  maxValue?: number;
  nutrientName?: string;
  stateOfFood?: string;
}

export interface SuperAdminBulkImportItem {
  payload: Record<string, unknown>;
  recipeUoms: SuperAdminBulkRecipeUom[];
  assignment?: SuperAdminBulkAssignment | null;
}

export interface SuperAdminBulkAssignment {
  mode: 'maker-and-checker' | 'checker-only';
  makerEmail?: string | null;
  checkerEmail?: string | null;
  dueDate?: string | null;
  priority?: string;
  notes?: string | null;
}

export interface SuperAdminBulkRowSelections {
  status: string;
  healthColorCodeId?: string | null;
  dietaryPreferenceId?: number | null;
  mealTypeIds: number[];
  sourceId?: number | null;
  recipeCategoryIds: number[];
  mealPreferenceIds: number[];
  foodGroupIds: number[];
  faoFoodGroupIds: number[];
  cookingMethodIds: number[];
  utensilIds: number[];
  equipmentIds: number[];
  tasteIds: number[];
  textureIds: number[];
  flavourIds: number[];
  aromaIds: number[];
  cuisineContinentIds: number[];
  cuisineRegionLabels: string[];
  cuisineSubRegionIds: number[];
  cuisineGeoTagIds: number[];
  occasionIds: number[];
  locationIds: number[];
  seasonIds: number[];
  allergenContainsIds: number[];
  allergenFreeIds: number[];
  intoleranceContainsIds: number[];
  intoleranceFreeIds: number[];
  diseaseIds: number[];
  courseIds: number[];
  storageTypeId?: number | null;
  shelfLifeUnit?: string | null;
  reheatMethodId?: number | null;
  reheatTemperatureUnit?: string | null;
  /** Uploaded recipe thumbnail URL (set in preview table). */
  thumbnailImagePath?: string | null;
  thumbnailUploading?: boolean;
  /** Parsed from CSV — reference only; use recipeIngredients for save payload. */
  ingredients: SuperAdminBulkIngredientDraft[];
  recipeIngredients: SuperAdminBulkRecipeIngredient[];
  recipeUoms: SuperAdminBulkRecipeUom[];
  stateOfProduct: string;
  generatedFssaiNutrientTags: SuperAdminBulkGeneratedFssaiTag[];
  /** Filled when status is Assigned to Maker / Assigned to Checker. */
  assignment?: SuperAdminBulkAssignment | null;
}

export interface SuperAdminBulkParsedText {
  recipeName: string;
  originalName: string;
  alternateName: string;
  numberOfServes: string;
  referenceUrl: string;
  recipeDescription: string;
  prepTime: string;
  soakingTime: string;
  fermentingTime: string;
  bakingTime: string;
  cookTime: string;
  professionalNotes: string;
  chefAuthorNotes: string;
  chefAuthorTips: string;
  chefMethodProcesses: string;
  /** Free-text remark from Excel (e.g. mapping note). */
  remark: string;
  ingredientsRaw: string;
  shelfLife: string;
  storageInstructions: string;
  reheatTemperature: string;
  reheatingInstructions: string;
  ccpNotes: string;
  costingNotes: string;
  videoReferenceUrl: string;
}

export interface SuperAdminBulkPreviewRow {
  rowNumber: number;
  valid: boolean;
  configured: boolean;
  error?: string;
  cells: Record<string, string>;
  display: Record<string, string>;
  parsed: SuperAdminBulkParsedText;
  selections: SuperAdminBulkRowSelections;
  payload?: Record<string, unknown>;
}

export interface SuperAdminBulkParseResult {
  rows: SuperAdminBulkPreviewRow[];
  validCount: number;
  errorCount: number;
  totalCount: number;
  fileError?: string;
}

export interface SuperAdminBulkLookups {
  healthColorCodes: SuperAdminBulkHealthColorOption[];
  dietaryPreferences: SuperAdminBulkLookupOption[];
  mealTypes: SuperAdminBulkLookupOption[];
  sources: SuperAdminBulkLookupOption[];
  recipeCategories: SuperAdminBulkLookupOption[];
  mealPreferences: SuperAdminBulkLookupOption[];
  foodGroups: SuperAdminBulkLookupOption[];
  faoFoodGroups: SuperAdminBulkLookupOption[];
  cookingMethods: SuperAdminBulkLookupOption[];
  utensils: SuperAdminBulkLookupOption[];
  equipment: SuperAdminBulkLookupOption[];
  tastes: SuperAdminBulkLookupOption[];
  textures: SuperAdminBulkLookupOption[];
  flavours: SuperAdminBulkLookupOption[];
  aromas: SuperAdminBulkLookupOption[];
  continents: SuperAdminBulkLookupOption[];
  occasions: SuperAdminBulkLookupOption[];
  locations: SuperAdminBulkLookupOption[];
  seasons: SuperAdminBulkLookupOption[];
  allergens: SuperAdminBulkLookupOption[];
  allergenFree: SuperAdminBulkLookupOption[];
  intolerances: SuperAdminBulkLookupOption[];
  intoleranceFree: SuperAdminBulkLookupOption[];
  diseases: SuperAdminBulkLookupOption[];
  courses: SuperAdminBulkLookupOption[];
  storageTypes: SuperAdminBulkLookupOption[];
  reheatMethods: SuperAdminBulkLookupOption[];
}

export const SUPER_ADMIN_RECIPE_STATUS_OPTIONS = [
  'Draft',
  'To be assigned',
  'Assigned to Maker',
  'Save as Draft',
  'First review',
  'Assigned to Checker',
  'Corrections Needed',
  'Corrections Done',
  'Checked',
  'Published',
];

export const SUPER_ADMIN_SHELF_LIFE_UNITS = ['Hours', 'Minutes', 'Days'];
export const SUPER_ADMIN_TEMPERATURE_UNITS = ['°C', '°F'];

/**
 * Bulk-upload template columns (CSV / XLSX).
 * Fill name columns and/or * Ids — values that match master auto-fill Configure Row tabs.
 * No columns are required (empty Name → Untitled Recipe on import).
 */
export const SUPER_ADMIN_RECIPE_TEMPLATE_HEADERS = [
  // Core
  'Recipe Name',
  'Original Name',
  'Alternate Name',
  'Serves',
  'Reference URL',
  'Description',
  'Prep Time Minutes',
  'Soaking Time Minutes',
  'Fermenting Time Minutes',
  'Baking Time Minutes',
  'Cook Time Minutes',
  'Video Reference URL',
  'Costing Notes',
  'Remark',

  // 1. Classification (name and/or Ids)
  'Health Color Code',
  'Health Color Code Ids',
  'Dietary Preference',
  'Diet Preference Ids',
  'Is Veg',
  'Meal Type',
  'Meal Type Ids',
  'Source',
  'Link Source Ids',
  'Recipe Category',
  'Category Ids',
  'Meal Preference',
  'Meal Preference Ids',
  'Food Group',
  'Food Group Ids',
  'Fao Food Group',
  'Fao Food Group Ids',

  // 2. Cooking
  'Cooking Method',
  'Cooking Method Ids',
  'Utensils',
  'Utensil Ids',
  'Equipment',
  'Equipment Ids',
  'Utensil Equipment Ids',

  // 3. Sensory
  'Taste',
  'Taste Ids',
  'Texture',
  'Texture Ids',
  'Flavour',
  'Flavour Ids',
  'Aroma',
  'Aroma Ids',

  // 4. Geo / Cuisine
  'Cuisine Continent',
  'Cuisine Continent Ids',
  'Cuisine Region',
  'Cuisine Sub Region Ids',
  'Cuisine Sub Sub Region Ids',
  'Occasion',
  'Occasion Ids',
  'Location',
  'Location Ids',
  'Season',
  'Season Ids',

  // 5. Allergens
  'Allergen',
  'Allergen Ids',
  'Allergen Free',
  'Allergen Free Ids',
  'Intolerance',
  'Intolerance Ids',
  'Intolerance Free',
  'Intolerance Free Ids',
  'Disease',
  'Disease Ids',
  'Course',
  'Course Ids',

  // 6. Notes / Chef
  'Professional Notes',
  'Chef Author Notes',
  'Chef Author Tips',
  'Chef Method Processes',

  // 7. Ingredients — "Name qty unit | Name qty unit" or "Name|qty|grams;;…"
  'Ingredients',

  // 8. Storage
  'Storage Type',
  'Storage Reheating Storage Type ID',
  'Shelf Life',
  'Storage Reheating Shelf Life',
  'Storage Reheating Shelf Life Unit',
  'Storage Instructions',
  'Storage Reheating Storage Instructions',
  'Reheat Method',
  'Storage Reheating Reheat Method ID',
  'Reheat Temperature',
  'Storage Reheating Reheat Temperature',
  'Reheat Temperature Unit',
  'Storage Reheating Reheat Temperature Unit',
  'Reheating Instructions',
  'Storage Reheating Reheating Instructions',
  'Ccp Notes',
  'Storage Reheating Ccp Notes',
  'State Of Product',

  // 9. Media
  'Thumbnail Image Path',
  'Main Image Path',

  // 10. Status (unknown values import as Draft)
  'Status',
];

export const SUPER_ADMIN_RECIPE_METADATA_INCLUDE =
  'seasons,occasions,flavours,textures,cookingMethods,categories,cuisines,courses,diseases,faoFoodGroups,foodGroups,utensils,equipment,storageTypes,reheatingMethods,aromas,allergens,allergenFree,intolerances,intoleranceFree,dietaryPreferences,mealTypes,mealPreferences,recipeLinkSources,tastes,locations';

export function createDefaultSuperAdminBulkSelections(): SuperAdminBulkRowSelections {
  return {
    status: 'Draft',
    mealTypeIds: [],
    recipeCategoryIds: [],
    mealPreferenceIds: [],
    foodGroupIds: [],
    faoFoodGroupIds: [],
    cookingMethodIds: [],
    utensilIds: [],
    equipmentIds: [],
    tasteIds: [],
    textureIds: [],
    flavourIds: [],
    aromaIds: [],
    cuisineContinentIds: [],
    cuisineRegionLabels: [],
    cuisineSubRegionIds: [],
    cuisineGeoTagIds: [],
    occasionIds: [],
    locationIds: [],
    seasonIds: [],
    allergenContainsIds: [],
    allergenFreeIds: [],
    intoleranceContainsIds: [],
    intoleranceFreeIds: [],
    diseaseIds: [],
    courseIds: [],
    thumbnailImagePath: null,
    thumbnailUploading: false,
    ingredients: [],
    recipeIngredients: [],
    recipeUoms: [],
    stateOfProduct: 'Solid',
    generatedFssaiNutrientTags: [],
    assignment: null,
  };
}
